import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { useAuth } from './auth/AuthProvider';
import { CAPABILITY_LABELS, useHasCapability, type AdminCapability } from './auth/usePermissions';
import { AdminLayout } from './components/AdminLayout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';

// 新增业务路由全部懒加载（15 号文档第 9 节），重依赖随页面单独拆包
const ContentListPage = lazy(() => import('./pages/contents/ContentListPage'));
const ContentFormPage = lazy(() => import('./pages/contents/ContentFormPage'));
const TagListPage = lazy(() => import('./pages/tags/TagListPage'));
const KolListPage = lazy(() => import('./pages/kols/KolListPage'));
const KolApplicationPage = lazy(() => import('./pages/kols/KolApplicationPage'));
const KolArticleAuditPage = lazy(() => import('./pages/kols/KolArticleAuditPage'));
const TopicListPage = lazy(() => import('./pages/topics/TopicListPage'));
const TopicEditPage = lazy(() => import('./pages/topics/TopicEditPage'));
const HomeConfigPage = lazy(() => import('./pages/home/HomeConfigPage'));
const ImportListPage = lazy(() => import('./pages/imports/ImportListPage'));
const ImportDetailPage = lazy(() => import('./pages/imports/ImportDetailPage'));
// M2 活动管理
const ActivityListPage = lazy(() => import('./pages/activities/ActivityListPage'));
const ActivityFormPage = lazy(() => import('./pages/activities/ActivityFormPage'));
const ActivityRegistrationPage = lazy(() => import('./pages/activities/ActivityRegistrationPage'));
// M4 社区治理
const ModerationQueuePage = lazy(() => import('./pages/community/ModerationQueuePage'));
const ReportHandlePage = lazy(() => import('./pages/community/ReportHandlePage'));
const SensitiveWordPage = lazy(() => import('./pages/community/SensitiveWordPage'));
const CommunityIdentityPage = lazy(() => import('./pages/community/CommunityIdentityPage'));
// M5 运营数据
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'));
// OPT-04/05/06/09：O2 后台运营闭环
const UserListPage = lazy(() => import('./pages/users/UserListPage'));
const AgreementListPage = lazy(() => import('./pages/agreements/AgreementListPage'));
const AdminAccountListPage = lazy(() => import('./pages/admin-accounts/AdminAccountListPage'));
const AuditLogPage = lazy(() => import('./pages/audit/AuditLogPage'));
const ServiceConfigPage = lazy(() => import('./pages/service-config/ServiceConfigPage'));
const FeedbackListPage = lazy(() => import('./pages/feedback/FeedbackListPage'));
// O3 运营闭环：票种 / 会员套餐 / 退款 / 社区话题
const TicketTypesPage = lazy(() => import('./pages/activities/TicketTypesPage'));
const MembershipPlanListPage = lazy(
  () => import('./pages/membership-plans/MembershipPlanListPage'),
);
const RefundListPage = lazy(() => import('./pages/refunds/RefundListPage'));
const CommunityTopicListPage = lazy(
  () => import('./pages/community/topics/CommunityTopicListPage'),
);
// B7 借阅域（33 号 §7.1/§7.2）
const LibraryBooksPage = lazy(() => import('./pages/library/LibraryBooksPage'));
const BookCopyListPage = lazy(() => import('./pages/library/BookCopyListPage'));
const AuthorsPage = lazy(() => import('./pages/library/AuthorsPage'));
const DonorsPage = lazy(() => import('./pages/library/DonorsPage'));
const BorrowingListPage = lazy(() => import('./pages/library/BorrowingListPage'));
const OverdueBoardPage = lazy(() => import('./pages/library/OverdueBoardPage'));
const ReservationListPage = lazy(() => import('./pages/library/ReservationListPage'));
const LibrarySettingsPage = lazy(() => import('./pages/library/LibrarySettingsPage'));

function ProtectedLayout(): React.ReactElement {
  const { admin, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!admin) {
    return <Navigate to="/login" replace />;
  }
  return <AdminLayout />;
}

function PageFallback(): React.ReactElement {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
      <Spin size="large" />
    </div>
  );
}

/**
 * 403 页面：直接输入 URL 但角色无权限时展示明确提示，而不是空白页。
 * 数据边界仍由服务端 Guard 决定（前端隐藏入口不等于获得数据）。
 */
export function ForbiddenPage({ capability }: { capability: AdminCapability }): React.ReactElement {
  const { admin } = useAuth();
  const navigate = useNavigate();
  return (
    <Result
      status="403"
      title="403"
      subTitle={`当前后台角色（${admin?.roles.join('、') ?? '未登录'}）无权访问「${
        CAPABILITY_LABELS[capability]
      }」页面。`}
      extra={
        <Button type="primary" onClick={() => navigate('/')}>
          返回概览
        </Button>
      }
    />
  );
}

/** 路由能力守卫：能力单一来源为 auth/usePermissions.ts */
export function RequireCapability({
  capability,
  children,
}: {
  capability: AdminCapability;
  children: React.ReactElement;
}): React.ReactElement {
  const allowed = useHasCapability(capability);
  if (!allowed) {
    return <ForbiddenPage capability={capability} />;
  }
  return children;
}

export function App(): React.ReactElement {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/contents" element={<ContentListPage />} />
          <Route path="/contents/new" element={<ContentFormPage />} />
          <Route path="/contents/:id/edit" element={<ContentFormPage />} />
          <Route path="/tags" element={<TagListPage />} />
          <Route path="/kols" element={<KolListPage />} />
          <Route path="/kols/applications" element={<KolApplicationPage />} />
          <Route path="/kols/articles" element={<KolArticleAuditPage />} />
          <Route path="/topics" element={<TopicListPage />} />
          <Route path="/topics/:id/edit" element={<TopicEditPage />} />
          <Route path="/home-config" element={<HomeConfigPage />} />
          <Route path="/imports" element={<ImportListPage />} />
          <Route path="/imports/:id" element={<ImportDetailPage />} />
          <Route path="/activities" element={<ActivityListPage />} />
          <Route path="/activities/new" element={<ActivityFormPage />} />
          <Route path="/activities/:id/edit" element={<ActivityFormPage />} />
          <Route path="/activities/:id/registrations" element={<ActivityRegistrationPage />} />
          <Route path="/activities/:id/ticket-types" element={<TicketTypesPage />} />
          <Route path="/community/moderation" element={<ModerationQueuePage />} />
          <Route path="/community/reports" element={<ReportHandlePage />} />
          <Route path="/community/sensitive-words" element={<SensitiveWordPage />} />
          <Route path="/community/identity" element={<CommunityIdentityPage />} />
          <Route
            path="/community/topics"
            element={
              <RequireCapability capability="MANAGE_COMMUNITY_TOPICS">
                <CommunityTopicListPage />
              </RequireCapability>
            }
          />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route
            path="/users"
            element={
              <RequireCapability capability="VIEW_USERS">
                <UserListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/membership-plans"
            element={
              <RequireCapability capability="MANAGE_MEMBERSHIP_PLANS">
                <MembershipPlanListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/refunds"
            element={
              <RequireCapability capability="HANDLE_REFUNDS">
                <RefundListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/agreements"
            element={
              <RequireCapability capability="MANAGE_AGREEMENTS">
                <AgreementListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/admin-accounts"
            element={
              <RequireCapability capability="MANAGE_ADMIN_ACCOUNTS">
                <AdminAccountListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/audit"
            element={
              <RequireCapability capability="VIEW_AUDIT">
                <AuditLogPage />
              </RequireCapability>
            }
          />
          <Route
            path="/service-config"
            element={
              <RequireCapability capability="MANAGE_SERVICE_CONFIG">
                <ServiceConfigPage />
              </RequireCapability>
            }
          />
          <Route
            path="/feedback"
            element={
              <RequireCapability capability="HANDLE_FEEDBACK">
                <FeedbackListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/books"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <LibraryBooksPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/copies"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <BookCopyListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/authors"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <AuthorsPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/donors"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <DonorsPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/borrowings"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <BorrowingListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/overdue"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <OverdueBoardPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/reservations"
            element={
              <RequireCapability capability="MANAGE_LIBRARY">
                <ReservationListPage />
              </RequireCapability>
            }
          />
          <Route
            path="/library/settings"
            element={
              <RequireCapability capability="MANAGE_LIBRARY_SETTINGS">
                <LibrarySettingsPage />
              </RequireCapability>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
